import React, { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react'
import DatePicker from 'react-datepicker'
import ReactTooltip from 'react-tooltip'
import { SupervisionStatus } from '../SupervisionStatus/SupervisionStatus'
import {
  CompletionStatusWrapper,
  ItemLabel,
  DatePickerWrapper,
  FilesSection,
  TimeSlotSection,
  FileWrapper,
  FileAction,
  Title,
  ActionContainer,
  ActionButton,
  SummarySection,
  SummaryTextarea,
  AttentionWarning,
  DatepickerActionBar,
  CompletedSupervisionEmpty,
  DatepickerActionButton,
} from './SupervisionManagementModal.styles'
import { ExpansionPanel, ExpansionPanelSummary, ExpansionPanelDetails, Typography, withStyles } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import styled from 'styled-components'
import { DownloadIcon } from 'uiKit/icons/DownloadIcon'
import { ExternalLinkIcon } from 'uiKit/icons/ExternalLinkIcon'
import './react-datepicker.css'
import { SupervisionStatuses } from '../../helpers/constants'
import { EditIcon } from 'uiKit/icons/EditIcon'
import moment from 'moment'
import { completeSupervision, editSupervision, getCurrentSession, getSupervisionSessions } from '../../api/usersApi'
import { alertError } from '../../../../api'
import { CurrentSessionType, UserType } from '../../model/usersModel'
import LoaderScreen from 'uiKit/loaders/loaderScreen'
import { downloadSupervisionResultHandler, downloadSupervisionTranscript } from '../../helpers/supervisionFileHelper'
import SubmitButton from 'uiKit/buttons/SubmitButton'
import { BOT_ID, TEMPLATE_ID } from 'constants/numeric'

interface Props {
  botId: string
  selectedUser: UserType
  currentSession: CurrentSessionType
  setCurrentSession: Dispatch<SetStateAction<CurrentSessionType>>
  hasExtraUpdate: boolean
  setHasExtraUpdate: Dispatch<SetStateAction<boolean>>
  isPrimaryButtonDisabled: boolean
}

// Custom styled ExpansionPanelSummary with proper content styling - similar to Questions.tsx
const StyledExpansionPanelSummary = withStyles({
  root: {
    minHeight: '56px',
    '&$expanded': {
      minHeight: '56px',
    },
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    margin: '0',
    '&$expanded': {
      margin: '0',
    },
    '& > :last-child': {
      paddingRight: 0,
    },
  },
  expanded: {},
})(ExpansionPanelSummary)

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
`

const ReportContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;
  flex-wrap: wrap;
  margin: 20px 0 10px 0;
`

const ReportItem = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 140px;
`

const ReportLabel = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: var(--color-text-form-label);
  margin-bottom: 10px;
`

const ReportValue = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: var(--color-text-primary);
`

const PendingStatus = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: var(--color-dark-gray);
`

const SubmittedStatus = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: var(--color-text-success);
`

const ViewDetailContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 20px;
  min-width: 110px;
  justify-content: flex-end;
  white-space: nowrap;
`

interface SupervisionReportProps {
  data?: CurrentSessionType
}

const SupervisionReportPanel: FC<SupervisionReportProps> = ({ data, children }) => {
  const [expanded, setExpanded] = useState<boolean>(false)

  const handleChange = (event, isExpanded) => {
    setExpanded(isExpanded)
  }

  const { scheduledAt, deadlineAt, status, summary, template } = data || {}

  return (
    <ExpansionPanel
      expanded={expanded}
      onChange={(event, isExpanded) => handleChange(event, isExpanded)}
      style={{
        borderRadius: '10px',
        border: '1px solid var(--color-issabeline-grey)',
        overflow: 'visible',
        boxShadow: '0px 0px 5.2px 2px #86868626',
        marginBottom: '15px',
        width: '100%',
      }}>
      <StyledExpansionPanelSummary
        expandIcon={null}
        classes={{
          content: 'expansionSummaryCurrentContent',
          expanded: 'expansionSummaryCurrentExpanded',
        }}
        style={{
          backgroundColor: 'white',
          padding: '16px',
          minHeight: '75px',
          borderBottom: expanded ? '1px solid var(--color-issabeline-grey)' : 'none',
        }}>
        <div>
          <FlexContainer style={{ justifyContent: 'space-between' }}>
            <Typography
              style={{
                fontWeight: 700,
                fontSize: '18px',
                color: 'var(--color-charcoal)',
              }}>
              {template?.name}
            </Typography>
            <ViewDetailContainer>
              <Typography
                style={{
                  fontSize: '16px',
                  color: 'var(--color-text-form-label)',
                  marginRight: '5px',
                }}>
                View detail
              </Typography>
              {expanded ? (
                <ExpandLessIcon style={{ fontSize: '20px', color: 'var(--color-text-form-label)' }} />
              ) : (
                <ExpandMoreIcon style={{ fontSize: '20px', color: 'var(--color-text-form-label)' }} />
              )}
            </ViewDetailContainer>
          </FlexContainer>
          {!expanded && (
            <ReportContainer>
              <ReportItem>
                <ReportLabel>Completion status</ReportLabel>
                <SupervisionStatus type={status} />
              </ReportItem>
              <ReportItem>
                <ReportLabel>Deadline</ReportLabel>
                <ReportValue>{deadlineAt ? moment(deadlineAt).format('DD/MM/YYYY') : '-'}</ReportValue>
              </ReportItem>
              <ReportItem>
                <ReportLabel>Available from</ReportLabel>
                <ReportValue>{scheduledAt ? moment(scheduledAt).format('DD/MM/YYYY') : '-'}</ReportValue>
              </ReportItem>
              <ReportItem>
                <ReportLabel>Manager summary</ReportLabel>
                <SupervisionStatus
                  type={summary?.hasManagerSummary ? SupervisionStatuses.SUBMITTED : SupervisionStatuses['PENDING']}
                />
              </ReportItem>
            </ReportContainer>
          )}
        </div>
      </StyledExpansionPanelSummary>
      <ExpansionPanelDetails
        style={{
          padding: '20px',
          backgroundColor: 'white',
          display: 'block',
          overflowY: 'auto',
        }}>
        <div>{children}</div>
      </ExpansionPanelDetails>
    </ExpansionPanel>
  )
}

export const CurrentSupervisionTab: FC<Props> = ({
  botId,
  selectedUser,
  currentSession,
  setCurrentSession,
  hasExtraUpdate,
  setHasExtraUpdate,
  isPrimaryButtonDisabled,
}) => {
  const [isOpenDate, setIsOpenDate] = useState<Record<string, { deadlineAt: boolean; scheduledAt: boolean }>>({})
  const [selectedDate, setSelectedDate] = useState<Record<string, { deadlineAt: string; scheduledAt: string }>>({})
  const [isLoadingCurrentSession, setIsLoadingCurrentSession] = useState(true)
  const [supervisionSessions, setSupervisionSessions] = useState<CurrentSessionType[]>([])
  // Convert UI state variables to session-specific functions
  const getIsSummaryDisabled = (session: CurrentSessionType) => {
    return [SupervisionStatuses.UPCOMING, SupervisionStatuses.TO_BE_COMPLETED, SupervisionStatuses.OVERDUE].includes(
      session.status,
    )
  }

  const getIsTextAreaDisabled = (session: CurrentSessionType) => {
    return session.status !== SupervisionStatuses.SUBMITTED
  }

  const getIsDatepickerDisabled = (session: CurrentSessionType) => {
    return session.status === SupervisionStatuses.SUBMITTED
  }

  const getSummaryTitle = (session: CurrentSessionType) => {
    return session.outputFormFileName || 'Unavailable'
  }

  const getTranscriptTitle = (session: CurrentSessionType) => {
    return session.transcriptFileName || 'Unavailable'
  }

  // We've fully migrated to session-specific functions, so we no longer need these backward compatibility variables

  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal

    // Use Promise.all to handle both requests together
    const fetchData = async () => {
      try {
        if (signal.aborted) return

        const [supervisionsResult] = await Promise.all([
          // getCurrentSession(botId, selectedUser.id, signal),
          getSupervisionSessions(botId, selectedUser.id, signal),
        ])

        if (signal.aborted) return

        // setCurrentSession(sessionResult)

        // Initialize date states for each session
        const dateOpenStates: Record<string, { deadlineAt: boolean; scheduledAt: boolean }> = {}
        const dateSelections: Record<string, { deadlineAt: string; scheduledAt: string }> = {}

        const supervisionSessions = supervisionsResult.map(session => {
          // Set initial state for each session
          dateOpenStates[session.id] = { deadlineAt: false, scheduledAt: false }
          dateSelections[session.id] = {
            deadlineAt: session.deadlineAt,
            scheduledAt: session.scheduledAt,
          }

          return {
            ...session,
            deadlineAtCustom: session.deadlineAt,
            scheduledAtCustom: session.scheduledAt,
          }
        })

        setIsOpenDate(dateOpenStates)
        setSelectedDate(dateSelections)
        setSupervisionSessions(supervisionSessions)
        setIsLoadingCurrentSession(false)
      } catch (error) {
        // Only log error if it's not an abort error
        if (error.name !== 'AbortError') {
          alertError('Error fetching supervision data')
        }
      }
    }

    fetchData()

    return () => {
      controller.abort()
    }
  }, [hasExtraUpdate, botId, selectedUser.id])

  const textareaChangeHandler = (sessionId: string, e) => {
    const value = e.target.value
    setSupervisionSessions(prevSessions => {
      return prevSessions.map(session => {
        if (session.id === sessionId) {
          return {
            ...session,
            summary: {
              ...session.summary,
              managerSummary: value,
            },
          }
        }
        return session
      })
    })

    // Also update current session if it's the same one
    if (currentSession.id === sessionId) {
      setCurrentSession(prevState => ({
        ...prevState,
        summary: {
          ...prevState.summary,
          managerSummary: value,
        },
      }))
    }
  }

  const applyHandler = (sessionId: string, type: string) => {
    setIsOpenDate(prevState => ({
      ...prevState,
      [sessionId]: {
        ...prevState[sessionId],
        [type]: false,
      },
    }))

    const dates: { scheduledAt?: string; deadlineAt?: string } = {}
    const session = supervisionSessions.find(s => s.id === sessionId)

    if (!session) return

    if (selectedDate[sessionId]?.scheduledAt !== session.scheduledAt) {
      dates.scheduledAt = selectedDate[sessionId]?.scheduledAt
    }
    if (selectedDate[sessionId]?.deadlineAt !== session.deadlineAt) {
      dates.deadlineAt = selectedDate[sessionId]?.deadlineAt
    }

    if (Object.keys(dates).length > 0) {
      editSupervision(botId, sessionId, dates).then(() => {
        setHasExtraUpdate(prevState => !prevState)
      })
    }
  }

  const onFocusHandler = (sessionId: string, type: string) => {
    setIsOpenDate(prevState => ({
      ...prevState,
      [sessionId]: {
        ...prevState[sessionId],
        [type]: true,
      },
    }))
  }

  const cancelHandler = (sessionId: string, type: string) => {
    const session = supervisionSessions.find(s => s.id === sessionId)
    if (!session) return

    setSelectedDate(prevState => ({
      ...prevState,
      [sessionId]: {
        ...prevState[sessionId],
        [type]: session[type],
      },
    }))

    setIsOpenDate(prevState => ({
      ...prevState,
      [sessionId]: {
        ...prevState[sessionId],
        [type]: false,
      },
    }))
  }
  const onChangeHandler = (sessionId: string, type: string, date: Date) => {
    setSelectedDate(prevState => ({
      ...prevState,
      [sessionId]: {
        ...prevState[sessionId],
        [type]: new Date(date).toISOString(),
      },
    }))
  }
  const datepickerIcon = (sessionId: string, type: string, isDisabled: boolean) => {
    return (
      <ActionButton disabled={isDisabled} onClick={() => onFocusHandler(sessionId, type)}>
        <EditIcon />
      </ActionButton>
    )
  }
  const getSessionDateState = (sessionId: string, type: string, session: CurrentSessionType) => {
    if (selectedDate[sessionId]?.[type]) {
      return new Date(selectedDate[sessionId][type])
    }
    return new Date(session[type])
  }

  const completeSupervisionHandler = (sessionId: string, summary: string) => {
    setIsLoadingCurrentSession(true)
    completeSupervision(botId, selectedUser.id, summary, sessionId)
      .then(() => {
        // Fetch the updated sessions after successful completion
        return getSupervisionSessions(botId, selectedUser.id)
      })
      .then(supervisionsResult => {
        // Initialize date states for each session
        const dateOpenStates: Record<string, { deadlineAt: boolean; scheduledAt: boolean }> = {}
        const dateSelections: Record<string, { deadlineAt: string; scheduledAt: string }> = {}

        const supervisionSessions = supervisionsResult.map(session => {
          // Set initial state for each session
          dateOpenStates[session.id] = { deadlineAt: false, scheduledAt: false }
          dateSelections[session.id] = {
            deadlineAt: session.deadlineAt,
            scheduledAt: session.scheduledAt,
          }

          return {
            ...session,
            deadlineAtCustom: session.deadlineAt,
            scheduledAtCustom: session.scheduledAt,
          }
        })

        setIsOpenDate(dateOpenStates)
        setSelectedDate(dateSelections)
        setSupervisionSessions(supervisionSessions)
        setHasExtraUpdate(prevState => !prevState)
        setIsLoadingCurrentSession(false)
      })
      .catch(error => {
        alertError('Error completing supervision')
        setIsLoadingCurrentSession(false)
      })
  }

  return (
    <>
      {isLoadingCurrentSession ? (
        <LoaderScreen
          size={{
            circleSize: 60,
            border: 8,
          }}
        />
      ) : supervisionSessions.length > 0 ? (
        supervisionSessions.map((session, index) => (
          <SupervisionReportPanel data={session} key={session.id}>
            <>
              <TimeSlotSection>
                <DatePickerWrapper>
                  <ItemLabel>Deadline</ItemLabel>
                  <DatePicker
                    open={isOpenDate[session.id]?.deadlineAt || false}
                    showIcon
                    dateFormat={'dd/MM/YYYY'}
                    selected={getSessionDateState(session.id, 'deadlineAt', session)}
                    onChange={date => onChangeHandler(session.id, 'deadlineAt', date)}
                    icon={datepickerIcon(session.id, 'deadlineAt', getIsDatepickerDisabled(session))}
                    shouldCloseOnSelect={false}
                    calendarStartDay={1}
                    onFocus={() => onFocusHandler(session.id, 'deadlineAt')}
                    onClickOutside={() => cancelHandler(session.id, 'deadlineAt')}
                    minDate={new Date()}
                    disabled={getIsDatepickerDisabled(session)}>
                    <DatepickerActionBar>
                      <DatepickerActionButton onClick={() => cancelHandler(session.id, 'deadlineAt')}>
                        Cancel
                      </DatepickerActionButton>
                      <DatepickerActionButton onClick={() => applyHandler(session.id, 'deadlineAt')} isPrimary={true}>
                        Apply
                      </DatepickerActionButton>
                    </DatepickerActionBar>
                  </DatePicker>
                </DatePickerWrapper>
                <DatePickerWrapper>
                  <ItemLabel>
                    Available from
                    <ReactTooltip
                      className="modal-tooltip"
                      place="top"
                      type="dark"
                      effect="solid"
                      id="AvailableАromTooltip-1"
                      show={true}>
                      Care Professional will be able to access their supervision from this date.
                    </ReactTooltip>
                    <img
                      style={{ cursor: 'pointer', marginLeft: '10px' }}
                      data-tip
                      data-for="AvailableАromTooltip-1"
                      src="/images/platform/information-button.svg"
                      alt="info icon"
                    />
                  </ItemLabel>
                  <DatePicker
                    open={isOpenDate[session.id]?.scheduledAt || false}
                    showIcon
                    dateFormat={'dd/MM/YYYY'}
                    selected={getSessionDateState(session.id, 'scheduledAt', session)}
                    onChange={date => onChangeHandler(session.id, 'scheduledAt', date)}
                    icon={datepickerIcon(session.id, 'scheduledAt', getIsDatepickerDisabled(session))}
                    shouldCloseOnSelect={false}
                    calendarStartDay={1}
                    onFocus={() => onFocusHandler(session.id, 'scheduledAt')}
                    onClickOutside={() => cancelHandler(session.id, 'scheduledAt')}
                    minDate={new Date()}
                    maxDate={getSessionDateState(session.id, 'deadlineAt', session)}
                    disabled={getIsDatepickerDisabled(session)}>
                    <DatepickerActionBar>
                      <DatepickerActionButton onClick={() => cancelHandler(session.id, 'scheduledAt')}>
                        Cancel
                      </DatepickerActionButton>
                      <DatepickerActionButton onClick={() => applyHandler(session.id, 'scheduledAt')} isPrimary={true}>
                        Apply
                      </DatepickerActionButton>
                    </DatepickerActionBar>
                  </DatePicker>
                </DatePickerWrapper>
                <CompletionStatusWrapper>
                  <ItemLabel>Completion status</ItemLabel>
                  <SupervisionStatus type={session.status} />
                </CompletionStatusWrapper>
              </TimeSlotSection>
              <FilesSection>
                {botId === BOT_ID && session.template.id === TEMPLATE_ID ? null : (
                  <FileWrapper>
                    <ItemLabel>Supervision summary</ItemLabel>
                    <FileAction isDisabled={getIsSummaryDisabled(session)}>
                      <Title isDisabled={getIsSummaryDisabled(session)}>{getSummaryTitle(session)}</Title>
                      <ActionContainer>
                        <ActionButton
                          disabled={getIsSummaryDisabled(session)}
                          onClick={() =>
                            downloadSupervisionResultHandler(botId, session.id, session.outputFormFileName, false)
                          }>
                          <DownloadIcon />
                        </ActionButton>
                        <ActionButton
                          disabled={getIsSummaryDisabled(session)}
                          onClick={() =>
                            downloadSupervisionResultHandler(botId, session.id, session.outputFormFileName, true)
                          }>
                          <ExternalLinkIcon />
                        </ActionButton>
                      </ActionContainer>
                    </FileAction>
                  </FileWrapper>
                )}

                <FileWrapper>
                  <ItemLabel>Supervision transcript</ItemLabel>
                  <FileAction isDisabled={getIsSummaryDisabled(session)}>
                    <Title isDisabled={getIsSummaryDisabled(session)}>{getTranscriptTitle(session)}</Title>
                    <ActionContainer>
                      <ActionButton
                        disabled={getIsSummaryDisabled(session)}
                        onClick={() =>
                          downloadSupervisionTranscript(botId, session.id, session.transcriptFileName, false)
                        }>
                        <DownloadIcon />
                      </ActionButton>
                      <ActionButton
                        disabled={getIsSummaryDisabled(session)}
                        onClick={() =>
                          downloadSupervisionTranscript(botId, session.id, session.transcriptFileName, true)
                        }>
                        <ExternalLinkIcon />
                      </ActionButton>
                    </ActionContainer>
                  </FileAction>
                </FileWrapper>
              </FilesSection>
              <SummarySection>
                <ItemLabel>Manager’s summary</ItemLabel>
                <SummaryTextarea
                  placeholder="Type your comment here..."
                  disabled={getIsTextAreaDisabled(session)}
                  value={session.summary?.managerSummary || ''}
                  onChange={e => textareaChangeHandler(session.id, e)}
                />
                {!!session.summary?.managerSummary && (
                  <AttentionWarning>
                    * You won’t be able to edit your comment once you complete supervision. Please review it before
                    submission.
                  </AttentionWarning>
                )}
              </SummarySection>
              <FlexContainer style={{ justifyContent: 'flex-end', marginTop: '20px' }}>
                <SubmitButton
                  title="Complete supervision"
                  type="primary"
                  styles={{ borderRadius: 4 }}
                  onClick={() => completeSupervisionHandler(session.id, session.summary?.managerSummary)}
                  disabled={getIsTextAreaDisabled(session) || session.summary?.managerSummary?.length === 0}
                />
              </FlexContainer>
            </>
          </SupervisionReportPanel>
        ))
      ) : (
        <CompletedSupervisionEmpty>No current supervisions available.</CompletedSupervisionEmpty>
      )}
    </>
  )
}
